import {
  type getGetFrontRichiesteCsvCampiQueryOptions,
  type getGetFrontRichiesteGeojsonCampiQueryOptions,
  type getPostFrontRichiesteCsvMutationOptions,
  type getPostFrontRichiesteGeojsonMutationOptions,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { type GetFrontRichiesteParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteParams.ts';
import { ExportFileButton } from '@framework/common-web/export-file/ExportFileButton.tsx';
import {
  type ExportFileField,
  type ExportFileType,
} from '@framework/common-web/export-file/ExportFileTypes.ts';
import { mapSelectedFieldsToFileFields } from '@framework/common-web/export-file/mapSelectedFieldsToFileFields.tsx';
import { useExportFile } from '@framework/common-web/export-file/useExportFile.ts';
import { type ButtonProps } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

interface ExportSubmissionFileButtonProps {
  type: ExportFileType;
  getMutationOptions:
    | typeof getPostFrontRichiesteCsvMutationOptions
    | typeof getPostFrontRichiesteGeojsonMutationOptions;
  getFieldsQueryOptions:
    | typeof getGetFrontRichiesteCsvCampiQueryOptions
    | typeof getGetFrontRichiesteGeojsonCampiQueryOptions;
  fileExtension: string;
  query: GetFrontRichiesteParams;
  buttonType?: ButtonProps['type'];
  asMenuItem?: boolean;
}

export function ExportSubmissionFileButton(
  props: ExportSubmissionFileButtonProps,
) {
  const {
    type,
    getMutationOptions,
    getFieldsQueryOptions,
    fileExtension,
    query,
    buttonType,
    asMenuItem,
  } = props;
  const { t } = useTranslation();

  const fieldsQuery = useQuery<ExportFileField[]>(getFieldsQueryOptions());
  const allFields = fieldsQuery.data;

  const { onExport } = useExportFile({
    getMutationOptions,
    downloadApiOptions: {
      filename: t('export.file', `Richieste_{{data}}.{{fileExtension}}`, {
        data: dayjs().format('YYYY-MM-DD_HH_mm_ss'),
        fileExtension,
      }),
    },
    buildVariables: (fields, exportAll) => ({
      data: {
        ...query,
        visibleFields: mapSelectedFieldsToFileFields(fields, allFields),
        ...(exportAll ? { pagina: 0, limite: 1_000_000 } : {}),
      },
    }),
  });

  const fieldsConfiguration = useMemo(
    () => ({ fields: allFields, areLoading: fieldsQuery.isFetching }),
    [allFields, fieldsQuery.isFetching],
  );

  const pagination = useMemo(
    () => ({ page: (query.pagina ?? 0) + 1, pageSize: query.limite }),
    [query.pagina, query.limite],
  );

  return (
    <ExportFileButton
      type={type}
      entity="richieste"
      onExport={onExport}
      fieldsConfiguration={fieldsConfiguration}
      pagination={pagination}
      buttonType={buttonType}
      asMenuItem={asMenuItem}
    />
  );
}
